import React, { useCallback, useRef } from "react";
import { connect } from "react-redux";

import {
  createEditMarker,
  createStartEndMarker,
  createStopMarker,
  createUnassignedMarker
} from "../icons/createMarker";
import { routeColors } from "../data/constants";
import MarkerWrapper from "./MarkerWrapper";

function LeafletMarkers({
  markers,
  editId,
  isHover,
  routeIndex,
  routeId,
  index,
  snapToRoad,
  hasSolution,
  colorsObj,
  mapRef,
  pinpointDriver,
  isRouteExpanded,
  endLocationDefined,
  customCount,
  warnClick,
  routeStatus,
  plan
}) {
  colorsObj = colorsObj || {};
  const isStartEnd = useCallback(
    type => type === "start" || type === "end",
    []
  );

  const hideDetails =
    routeStatus === "in_progress" && !plan.ft_live_status_updates;

  const getColor = id => (colorsObj[id] >= 0 ? colorsObj[id] : 0);

  const color =
    routeIndex > -1
      ? routeColors[getColor(routeId) % routeColors.length]
      : undefined;

  const startLocation = useRef();

  const editMarker = (stop, index) =>
    routeIndex > -1
      ? createEditMarker(stop, index, color)
      : createUnassignedMarker(stop);

  const getMarker = (stop, index) => {
    let marker = "";
    if (isStartEnd(stop.type)) {
      marker = createStartEndMarker(stop, color, stop.type === "end");
    } else if (stop.status === "finished" && !hideDetails) {
      marker = createStopMarker(stop, index, color, "finished");
    } else if (stop.status === "skipped" && !hideDetails) {
      marker = createStopMarker(stop, index, color, "skipped");
    } else if (editId === stop.id) {
      marker = editMarker(stop, index);
    } else if (routeIndex === undefined || routeIndex === -1) {
      marker = createUnassignedMarker(stop);
    } else {
      marker = createStopMarker(stop, index, color);
    }

    return marker;
  };

  const getZindex = stop => {
    let zIndex = editId === stop.id ? 90000 : isRouteExpanded ? 70000 : 40001;

    if (stop.type === "start" || stop.type === "end") {
      if (!endLocationDefined && isRouteExpanded) {
        zIndex -= 20000;
      } else if (isRouteExpanded) {
        zIndex -= 0;
      } else {
        zIndex -= 10000;
      }
    }

    return zIndex;
  };

  let count = 0;

  return markers.map((stop, i) => {
    if (isStartEnd(stop.type) && !hasSolution) return null;
    if (
      stop.address &&
      (!pinpointDriver || pinpointDriver.id === stop.id) &&
      stop.type !== "break" &&
      stop.type !== "reload"
    ) {
      if (isHover) {
        // Marker hovered from sidebar
        return stop.type === "end" && endLocationDefined === false ? null : (
          <MarkerWrapper
            stop={stop}
            key={stop.id + "hover"}
            isEditing={false}
            routeIndex={routeIndex}
            routeId={routeId}
            isHover={isHover}
            snapToRoad={snapToRoad}
            map={mapRef}
            index={index}
            iconHtml={`<div class="marker-box${!snapToRoad ? " hover" : ""}">
              ${getMarker(stop, customCount || stop.count)}
            </div>`}
            pane={"hovered"}
            getZindex={() => 90000}
            handleClick={warnClick}
            isStartEnd={isStartEnd}
          />
        );
      } else {
        // Default markers in cluster

        // Calculate count (number to show on marker)
        if (
          stop.type !== "break" &&
          stop.type !== "start" &&
          stop.type !== "end"
        ) {
          count++;
        }

        if (stop.type === "start") {
          // keep track of start location, if end is the same, show single marker
          startLocation.current = {
            lat: stop.address.lat,
            lng: stop.address.lng
          };
        }

        const numberToPass = count;

        return stop.type === "end" &&
          ((startLocation.current?.lat === stop.address.lat &&
            startLocation.current?.lng === stop.address.lng) ||
            endLocationDefined === false) ? null : (
          <MarkerWrapper
            stop={stop}
            key={stop.id}
            map={mapRef}
            routeId={routeId}
            isEditing={editId === stop.id}
            routeIndex={routeIndex}
            index={index}
            iconHtml={`<div class="marker-box ${
              editId === stop.id ? "edit" : ""
            }">
              ${getMarker(stop, numberToPass)}
            </div>`}
            getZindex={getZindex}
            handleClick={warnClick}
            isStartEnd={isStartEnd}
          />
        );
      }
    } else {
      return null;
    }
  });
}

function mapStateToProps(state) {
  return {
    editId: state.extendable.type === "stops" && state.extendable.id,
    colorsObj: state.project.solution && state.project.solution.routeColors,
    pinpointDriver: state.map.pinpointDriver,
    hasSolution:
      state.project.solution &&
      state.project.solution.status !== "ERROR" &&
      state.project.solution.routes.length,
    plan:
      (state.auth.userData.organisationSite &&
        state.auth.userData.organisationSite.organisation.plan) ||
      {}
  };
}

export default connect(mapStateToProps, {})(LeafletMarkers);
